import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ApiClient from '../config/ApiClient';
import AthleteSessionsCompletedSection from './session-engagement/AthleteSessionsCompletedSection';
import AthleteTimelineSection from './session-engagement/AthleteTimelineSection';
import SessionDurationSection from './session-engagement/SessionDurationSection';
import SessionEngagementHeader from './session-engagement/SessionEngagementHeader';
import SlideDurationSection from './session-engagement/SlideDurationSection';
import SlideTypeSection from './session-engagement/SlideTypeSection';
import SlidesSeenSection from './session-engagement/SlidesSeenSection';
import { EngagementReportData } from './session-engagement/types';
import RecommendationsSection from './team-report/RecommendationsSection';

const SessionEngagementPage: React.FC = () => {
    const [data, setData] = useState<EngagementReportData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        ApiClient.get<EngagementReportData>('/api/session-engagement-report')
            .then((res) => {
                setData(res.data as EngagementReportData);
            })
            .catch((err) => {
                const msg = err?.response?.data?.detail || 'Failed to load engagement data';
                setError(msg);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">No engagement data available.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, md: 3 } }} className="session-engagement-report">
            <SessionEngagementHeader
                organizationName={data.header.organization_name}
                reportDate={data.header.report_date}
                planCount={data.header.plan_count}
                athleteCount={data.header.athlete_count}
            />
            <SessionDurationSection data={data.session_durations} />
            <SlideDurationSection data={data.slide_durations} />
            <SlideTypeSection data={data.slide_type_durations} />
            <SlidesSeenSection rows={data.slides_seen.rows} />
            <AthleteSessionsCompletedSection athletes={data.athlete_timeline.athletes} />
            <AthleteTimelineSection athletes={data.athlete_timeline.athletes} />
            <RecommendationsSection recommendations={data.recommendations} />
        </Box>
    );
};

export default SessionEngagementPage;
